<script setup>
// core
import {computed, ref} from "vue";

// components
import BaseCopyButton from "@/components/ui/BaseCopyButton.vue";

// fixed-length mask: deriving it from the password would leak its length
const PASSWORD_MASK = '••••••••••••'

const props = defineProps({
    extension: {
        type: Object,
        required: true
    },
    checked: {
        type: Boolean,
        default: false
    },
    toggleLoading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['toggle', 'edit', 'delete'])

const isPasswordVisible = ref(false)

const numbersCount = computed(() => props.extension.connected_numbers_count ?? 0)

const isLocked = computed(() => props.checked && numbersCount.value <= 1)
</script>

<template>
    <li class="sip-card" :class="{ 'is-checked': checked }">
        <span v-if="toggleLoading" class="spinner sip-card__spinner"></span>
        <div
            v-else
            class="custom-checkbox"
            :class="{ 'is-locked': isLocked }"
            :title="isLocked ? $t('telephony.sip.locked_hint') : ''"
        >
            <input
                type="checkbox"
                :id="`sip-checkbox-${extension.credentials?.username}`"
                :checked="checked"
                :disabled="isLocked"
                @change="emit('toggle', extension)"
            />
            <label :for="`sip-checkbox-${extension.credentials?.username}`">
                <i v-if="isLocked" class="bi bi-lock-fill"></i>
            </label>
        </div>

        <div class="sip-card__body">
            <p class="sip-card__name">{{ extension.name }}</p>

            <p
                class="sip-card__numline"
                :title="$t('telephony.sip.linked_numbers_hint', {count: numbersCount})"
            >
                <b class="item_title">{{ $t('telephony.sip.linked_numbers') }}</b>
                <span
                    class="sip-card__numvalue"
                    :class="{ 'is-empty': !numbersCount }"
                >{{ numbersCount }}</span>
            </p>

            <div class="sip-card__creds">
                <span class="sip-card__pair">
                    <b class="item_title">{{ $t('telephony.sip.login_label') }}</b>
                    <span class="sip-card__value">{{ extension.credentials?.username }}</span>
                    <BaseCopyButton
                        :value="extension.credentials?.username"
                        :title="$t('telephony.sip.copy_login')"
                        toast="telephony.sip.copied"
                    />
                </span>

                <span class="sip-card__pair">
                    <b class="item_title">{{ $t('telephony.sip.password_label') }}</b>
                    <span
                        class="sip-card__value sip-card__pass"
                        :class="{ 'is-visible': isPasswordVisible }"
                        :title="isPasswordVisible ? extension.credentials?.password : ''"
                    >{{ isPasswordVisible ? extension.credentials?.password : PASSWORD_MASK }}</span>
                    <button
                        type="button"
                        class="inside_block --sm"
                        :title="$t(isPasswordVisible ? 'telephony.sip.hide_password' : 'telephony.sip.show_password')"
                        @click="isPasswordVisible = !isPasswordVisible"
                    >
                        <i class="icon fas" :class="isPasswordVisible ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                    <BaseCopyButton
                        :value="extension.credentials?.password"
                        :title="$t('telephony.sip.copy_password')"
                        toast="telephony.sip.copied"
                    />
                </span>
            </div>
        </div>

        <div class="sip-card__actions">
            <button
                type="button"
                class="inside_block"
                :title="$t('common.edit')"
                @click="emit('edit', extension)"
            >
                <i class="icon fa fa-pencil"></i>
            </button>
            <button
                type="button"
                class="inside_block"
                :title="$t('common.delete')"
                @click="emit('delete', extension)"
            >
                <i class="icon fa fa-trash"></i>
            </button>
        </div>
    </li>
</template>

<style scoped lang="scss">
.sip-card {
    display: flex;
    align-items: center;
    gap: 14px;

    // html sets Figtree, which the project does not ship — without this the
    // card falls back to the system sans instead of Gilroy
    font-family: 'Gilroy', sans-serif;

    padding: 10px 14px;
    border: 1px solid #e9eaec;
    border-radius: 7px;
    background-color: #ffffff;
    transition: all 0.7s ease;

    // unchecked row pulls toward the checked look on hover
    &:hover {
        background-color: var(--block-color);
        border-color: var(--general-color);
    }

    &.is-checked {
        border-color: var(--general-color);
        background-color: var(--block-color);

        // and the checked one lightens, as .setting_block does in SettingsNumberDialog
        &:hover {
            background-color: var(--block-second-color);
        }
    }

    // the size of the checkbox it stands in for
    &__spinner {
        flex: 0 0 auto;
        width: 22px;
        height: 22px;
        border-width: 3px;
        margin: 0;
    }

    &__body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__name {
        margin: 0;
        padding: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.05rem;
        color: var(--text-color);
    }

    // how many numbers this trunk is bound to, on a line of its own
    &__numline {
        margin: 0;
        padding: 0;
        display: flex;
        align-items: baseline;
        gap: 6px;

        line-height: 19px;
        font-size: 14px;
        color: var(--text-third-color);
    }

    &__numvalue {
        letter-spacing: 0.05rem;
        font-weight: 700;
        color: var(--text-color);

        &.is-empty {
            font-weight: 500;
            color: var(--border-primary-color);
        }
    }

    // .info_item from SettingsNumberDialog
    &__creds {
        display: flex;
        align-items: center;
        gap: 6px 14px;
        flex-wrap: wrap;
        min-width: 0;

        line-height: 19px;
        font-size: 14px;
        color: var(--text-third-color);
    }

    // label, value and its buttons only ever wrap together
    &__pair {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }

    &__value {
        margin-right: 2px;
        letter-spacing: 0.05rem;
        white-space: nowrap;
    }

    // the mask needs its own tracking, otherwise the dots merge into a line
    &__pass {
        letter-spacing: 0.18em;
    }

    // a fixed, scrollable slot only while the password is on screen
    &__pass.is-visible {
        flex: 0 0 150px;
        width: 150px;
        overflow-x: auto;
        letter-spacing: 0.05rem;

        // no scrollbar: it eats 5px inside the field and lifts the text off the
        // label baseline. Wheel and trackpad scrolling still work.
        scrollbar-width: none;
        -ms-overflow-style: none;

        // the clipped tail is hinted with a fade instead
        -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);
        mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &__actions {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
    }
}

// .item_title from SettingsNumberDialog: the colon comes from ::after
.item_title {
    display: flex;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--border-primary-color);

    &::after {
        content: ":";
    }

}

// .custom-checkbox: same pattern as the project tables
.custom-checkbox {
    flex: 0 0 auto;
    display: inline-flex;

    input[type="checkbox"] {
        display: none;
    }

    label {
        display: inline-block;
        width: 25px;
        height: 25px;
        border: 1px solid lightgrey;
        cursor: pointer;
        position: relative;
        background-color: #ffffff;

        &::after {
            content: "";
            background-image: url("../../assets/tariffs/check.png");
            background-size: contain;
            width: 12px;
            height: 12px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: none;
        }
    }

    input[type="checkbox"]:checked + label::after {
        display: block;
    }

    &.is-locked label {
        cursor: not-allowed;

        display: flex;
        align-items: center;
        justify-content: center;

        &::after {
            content: none;
        }

        i {
            font-size: 15px;
            line-height: 1;
            color: var(--border-primary-color);
        }
    }
}

// .inside_block from SettingsNumberDialog
.inside_block {
    position: relative;
    min-width: 35px;
    width: 35px;
    height: 35px;
    max-width: none;
    flex: 0 0 auto;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 9px;
    border: 1px solid var(--general-color);
    background-color: #ffffff;
    cursor: pointer;
    transition: 0.25s;

    // font glyphs get sized by font-size only: a fixed box is narrower than
    // the glyph and pushes it off-centre. Explicit sizing is for svg.
    .icon {
        font-size: 18px;
        line-height: 1;
        fill: var(--general-color);
        color: var(--general-color);
        transition: 0.25s;
    }

    svg {
        width: 20px;
        height: 20px;
    }

    .spinner {
        width: 18px;
        height: 18px;
        margin: 0;
    }

    &:hover {
        background-color: var(--general-color);

        .icon {
            fill: #ffffff;
            color: #ffffff;
        }
    }

    &.--sm {
        min-width: 27px;
        width: 27px;
        height: 27px;
        border-radius: 7px;

        .icon {
            font-size: 13px;
            line-height: 1;
        }

        svg {
            width: 13px;
            height: 13px;
        }
    }
}

// breakpoints match SettingsNumberDialog
@media (max-width: 570px) {
    .sip-card {
        // checkbox and actions pin to the top once the body becomes multi-line
        align-items: flex-start;

        &__actions,
        &__spinner,
        .custom-checkbox {
            margin-top: 2px;
        }

        // every pair on its own row; values shrink instead of pushing buttons out
        &__creds {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }

        &__pair {
            width: 100%;
            min-width: 0;
        }

        &__value {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &__pass.is-visible {
            flex: 1 1 auto;
            width: auto;
            min-width: 0;
        }
    }
}

@media (max-width: 420px) {
    .sip-card {
        gap: 10px;
        padding: 10px;

        &__name {
            font-size: 15px;
        }

        &__actions {
            gap: 6px;

            .inside_block {
                min-width: 31px;
                width: 31px;
                height: 31px;
            }
        }
    }

    .item_title {
        font-size: 11px;
    }
}
</style>
